import getDeviceType from '@globals/utilities/GetDeviceType';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import jsonFetch from './jsonFetch';
import { getAppType } from './utilities/isMobileApp';

export default class Analytics {
	static Create() {
		const appStore = useAppStore();

		window.dataLayer = window.dataLayer || [];

		/* eslint-disable */
		const disableAnalytics = (navigator.userAgent.indexOf('appstore.oferteo') > -1 && window.localStorage.CordovaEnableTracking !== 'yes');
		window.addEventListener('RequestTrackingEvent', (e) => { window.localStorage.CordovaEnableTracking = e.detail ? 'yes' : 'no'; }, false);

		if (disableAnalytics) { return false; }
		this.GtmInit(appStore.googleAnalyticsData);

		return true;
	}

	/**
	 * Przysyła początkowe zdarzenie z wymiarami. Nie dołączamy kodu GTM, ponieważ jest on już dołączony w pliku `index.html`.
	 * @param dims Poczatkowe wymiary przesłane w `hello.ofer` w polu `analytics`.
	 */
	static async GtmInit(dims = {}) {
		const appTypeRaw = await getAppType();
		this.GtmDims({ ...dims, app_type: appTypeRaw === 'default' ? 'Web' : appTypeRaw }, false);
	}

	/** Ostatni stan wymiarów. */
	static GtmCurrentDims = {};

	/**
	 * Wysyła zdarzenie `ga_dims` dodając lub zastępując wymiary, o ile te uległy zmianie.
	 * @param dims Dodatkowe wymiary.
	 */
	static GtmDims(dims = {}, send = true) {
		const prev = JSON.stringify(this.GtmCurrentDims);
		this.GtmCurrentDims = { ...this.GtmCurrentDims, ...dims };

		// Wyślij wymiary, jeśli się zmieniły.
		if (prev !== JSON.stringify(this.GtmCurrentDims) && send) {
			window.dataLayer?.push({
				'event': 'ga_dims',
				'event_params': this.GtmCurrentDims,
			});
		}
	}

	/**
	 * Zwraca typ strony na potrzeby analityki.
	 * @param pageModule Obecny moduł (CustomerView, BuyerView, etc.)
	 * @param component Obecny komponent (Rfps, Profile, etc.)
	 * @param state Obecny stan strony
	 */
	static GtmPageType(pageModule:string, component:string, state:any) {
		let result = pageModule;

		// • LoginRegister, MyOferteo, BuyerView: typ strony odpowiada nazwie modułu, nic nie musimy robić
		// • Admin: strony administracyjne nie powinny wysyłać analityki, nic nie musimy robić
		switch (pageModule) {
			// • RfpForm: Składanie zapytania
			case 'Form': result = 'RfpForm'; break;
			// • RfpDetails: Szczegóły na publicznej liście (w module `PublicView` mamy tylko ten widok)
			// • StaticContent: Statyczne strony w module `PublicView`
			case 'PublicView': 
				result = 'RfpDetails';
				if (component === 'ContactPage') {
					result = 'StaticContent';
				}
				break;
			case 'CustomerView':
				result = 'MyOferteo';
				// • MyOferteoRfpDetails: Szczegóły w Moim Oferteo
				// • CustomerRfpList: Lista w Moim Oferteo
				if (component === 'Rfps' || component === 'Contacts') {
					result = `MyOferteoRfp${state.RfpId ? 'Details' : 'List'}`;
				}
				// • MyOferteoStaticContent: Statyczna strona w Moim Oferteo
				const staticContent = ['QuickPaymentsPage', 'RecommendationPage', 'PromotionPage'];
				if (staticContent.includes(component)) {
					result = 'MyOferteoStaticContent';
				}
				break;
			default: result = pageModule; break;
		}

		// Zwróć typ strony
		return result;
	}

	/**
	 * Wysyła zdarzenie o podanej nazwie.
	 * @param eventName Nazwa zdarzenia.
	 * @param eventParams Parametry zdarzenia. Jeżeli nie zostały podane, wyślij pusty obiekt.
	 */
	static sendGAEvent(
		eventName: string,
		eventParams: Record<string, string | number | boolean> = {}
	) {
		const { category_id, ...params } = eventParams;
		if (category_id) {
			params.category_id =
				typeof category_id === 'string' ? parseInt(category_id) : category_id;
		}

		window.dataLayer?.push({
			event: 'ga_event',
			event_name: eventName,
			...(Object.keys(params).length > 0 ? { event_params: params } : {}),
		});
	}

	static MyOferteoTrack(group, label) {
		fetch(`/myoferteo/konto-json/log-myoferteo-action-stats.ofer?step=${group}&action=${label}`);
	}

	static NoteUserActionByName(actionType: string, actionGroup: string, actionParameters = {}) {
		jsonFetch('/user/note-user-action-by-name.do', {
			method: 'POST',
			body: { actionType, actionGroup, actionParameters },
		});
	}

	static NoteModalAction(actionType: string, modalName: string, actionParameters = {}) {
		Analytics.NoteUserActionByName(actionType, 'MODAL', { modalName, ...actionParameters });
	};

	/**
	 * Wysyła informacje analityczne poprzez Websocket.
	 * Robimy to w ten sposób, aby utrzymać liczbę requestów w ryzach.
	 * @param id
	 * @param action
	 * @param step
	 * @param source
	 * @param other
	 * @returns
	 */
	static Websocket(
		id = -1,
		action = '',
		step = '',
		source = '',
		other = {},
	) {
		const userStore = useUserStore();

		// TODO: Przenieść do komponentu Rfp.vue
		const ws = Analytics.Socket;
		if (!ws) {
			return;
		}

		const data = {
			idRfp: id,
			idCustomer: userStore.customerId,
			action,
			step,
			source,
			deviceType: getDeviceType(),
			...other,
		};

		try {
			ws.Send({
				type: 'RfpTrack',
				data,
			});
		} catch (err) {
			console.warn('WebsocketAnalytics', err);
		}
	}
}
